import React from "react";
import { ClockIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  ShippingReportAudit,
  ShippingReportAuditChange,
} from "../../types/shippingReports";

const actionLabels: Record<string, string> = {
  created: "Created the report",
  prefilled_from_parent: "Pre-filled fields from the related record",
  updated: "Updated report fields",
  completed: "Marked the report completed",
  reopened: "Reopened the report",
  images_uploaded: "Uploaded pictures",
  image_updated: "Updated a picture",
  image_deleted: "Deleted a picture",
  images_reordered: "Reordered pictures",
  pdf_downloaded: "Downloaded the PDF",
  arrival_temperature_added: "Added an arrival temperature reading",
  arrival_temperature_updated: "Updated an arrival temperature reading",
  arrival_temperature_deleted: "Deleted an arrival temperature reading",
};

const fieldLabels: Record<string, string> = {
  arrival_at: "Arrival time",
  customer_name: "Customer name",
  carrier_name: "Carrier name",
  arrival_temperature_celsius: "Arrival temperature",
  driver_name: "Driver name",
  trailer_number: "Trailer number",
  trailer_plate_number: "Trailer plate number",
  started_at: "Start time",
  completed_at: "Completion time",
  departure_temperature_celsius: "Departure temperature",
  departure_at: "Departure time",
  notes: "Notes",
  caption: "Picture caption",
  type: "Picture type",
  images: "Pictures",
  image: "Picture",
  image_ids: "Picture order",
  parent: "Related record",
  temperature_reading: "Temperature reading",
  reading_id: "Reading",
  temperature_celsius: "Temperature",
  recorded_at: "Reading time",
  picture: "Evidence picture",
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "Blank";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return JSON.stringify(value);
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

function ChangeRow({
  field,
  change,
}: {
  field: string;
  change: ShippingReportAuditChange;
}) {
  return (
    <li className="grid gap-1 border-t border-slate-100 py-2 text-xs sm:grid-cols-[10rem_1fr]">
      <span className="font-medium text-slate-600">
        {fieldLabels[field] ?? field.replaceAll("_", " ")}
      </span>
      <span className="min-w-0 break-words text-slate-600">
        <span className="line-through decoration-slate-400">
          {formatValue(change.old)}
        </span>
        <span className="px-2 text-slate-400">→</span>
        <span className="font-medium text-slate-900">
          {formatValue(change.new)}
        </span>
      </span>
    </li>
  );
}

export default function ShippingReportAuditLog({
  audits,
  onClose,
}: {
  audits: ShippingReportAudit[];
  onClose?: () => void;
}) {
  return (
    <section className="flex h-full flex-col bg-white">
      <div className="flex items-start justify-between border-b border-slate-200 px-4 py-4 sm:px-6">
        <div>
          <div className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-900">Audit History</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Report changes and document activity, newest first.
          </p>
        </div>
        {onClose ? (
          <button
            type="button"
            aria-label="Close audit history"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        {audits.length === 0 ? (
          <p className="rounded-lg bg-slate-50 px-4 py-5 text-sm text-slate-500">
            No audit activity recorded yet.
          </p>
        ) : (
          <ol className="divide-y divide-slate-200">
            {audits.map((audit) => {
              const changes = Object.entries(audit.changes ?? {});
              return (
                <li key={audit.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-start">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {actionLabels[audit.action] ?? audit.action}
                      </p>
                      <p className="text-xs text-slate-500">
                        by {audit.actor_name || "Unknown user"}
                      </p>
                    </div>
                    <time className="text-xs text-slate-500">
                      {formatDate(audit.created_at)}
                    </time>
                  </div>
                  {changes.length > 0 ? (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs font-medium text-indigo-700">
                        View {changes.length} change{changes.length === 1 ? "" : "s"}
                      </summary>
                      <ul className="mt-1">
                        {changes.map(([field, change]) => (
                          <ChangeRow key={field} field={field} change={change} />
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
